// ═══════════════════════════════════════════════════════════════
// alcanceEmpresas.js — Helper para filtrar EMPRESAS por alcance del rol
// ═══════════════════════════════════════════════════════════════
//
// Bug #75: muchas páginas hacían `getDocs(EMPRESAS)` sin filtro y confiaban
// en las firestore.rules para limitar. La rule actual deja read a
// `isConsultor()` (= cualquier staff Milbrands), así que un Consultor con
// 1 empresa asignada veía las 5 empresas del sistema.
//
// Esta utility centraliza el filtrado:
//   - Quien ve todas (`veTodasLasEmpresas()`: Wizard / Operaciones) → query sin filtro.
//   - Resto (Consultor, Supervisor, Owner, etc) → query restringida a las
//     empresas en userDoc.empresaIds (denormalizado por trigger desde
//     /asignaciones). Chunks de 30 por límite del operador 'in'.
//
// Uso:
//   import { cargarEmpresasSegunAlcance } from '@core/lib/alcanceEmpresas'
//   const empresas = await cargarEmpresasSegunAlcance({ veTodasLasEmpresas, userDoc })
//   // empresas = [{ id, ...data }, ...]
//
// Para abrir UNA empresa (la ficha) no se baja el listado: se lee su documento,
// y sólo si está en el alcance.
//   const empresa = await leerEmpresaEnAlcance({ veTodasLasEmpresas, userDoc, empresaId })
//   if (!empresa) …   // fuera de alcance o no existe: para la pantalla es lo mismo
// ═══════════════════════════════════════════════════════════════

import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'

/**
 * Carga la lista de empresas que el user puede ver según su rol.
 * @param {object} args
 * @param {Function} args.veTodasLasEmpresas — `useAuth().veTodasLasEmpresas`. Obligatoria: sin ella, tira. Si true → sin filtro.
 * @param {object} args.userDoc — `useAuth().userDoc`. Lee `empresaIds[]`.
 * @returns {Promise<Array<{id: string, ...}>>}
 */
export async function cargarEmpresasSegunAlcance({ veTodasLasEmpresas, userDoc }) {
  if (veTodasLasEmpresas()) {
    const snap = await getDocs(collection(db, COLLECTIONS.EMPRESAS))
    return snap.docs.map(docConId)
  }
  const ids = userDoc?.empresaIds || []
  if (ids.length === 0) return []
  const chunks = []
  for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30))
  const out = []
  for (const c of chunks) {
    const q = query(collection(db, COLLECTIONS.EMPRESAS), where('__name__', 'in', c))
    const snap = await getDocs(q)
    snap.docs.forEach(d => out.push(docConId(d)))
  }
  return out
}

/**
 * Lee UNA empresa, sólo si está en el alcance (el de `cargarEmpresasSegunAlcance`).
 * Fuera de alcance no lee nada. Si la lectura falla, rechaza con el error de
 * Firestore: quien llama decide qué mostrar.
 * @returns {Promise<{id: string}|null>} `null` si está fuera de alcance o no existe.
 */
export async function leerEmpresaEnAlcance({ veTodasLasEmpresas, userDoc, empresaId }) {
  if (!veTodasLasEmpresas() && !(userDoc?.empresaIds || []).includes(empresaId)) return null
  const snap = await getDoc(doc(db, COLLECTIONS.EMPRESAS, empresaId))
  return snap.exists() ? docConId(snap) : null
}
